import Image from "next/image";
import Link from "next/link";

import { isProd } from "@/Env";
import {
  EXOSHOCK_POPUP_BUNDLE_SLUG,
  EXOSHOCK_POPUP_COPY_READY,
  EXOSHOCK_POPUP_CTA_HREF,
} from "@/constants/exoshock-popup";
import { useValidBundles } from "@/hooks/useValidBundles";
import { useLocale } from "@/store/locale-store";
import { isDismissedToday } from "@/store/popup-banner-store";
import { useExoshockPopupStore } from "@/store/exoshock-popup-store";
import { useTrans } from "@/utils/useTrans";
import type { PopupSlide } from "@/v2-components/ui/floating-popup";

/** 2026-10-30 시안 수정본의 새 아트 — 파일명을 바꿔 둔다(같은 이름 덮어쓰기는 `/_next/image` 캐시가 옛 그림을 준다). */
export const EXOSHOCK_POPUP_BG = "/images/event/exoshock-bundle-popup-bg.webp";

/**
 * 홈 팝업의 EXOSHOCK 슬라이드 제공자 — 할로윈(`useHalloween2026PopupSlides`) · VRChat 과 같은 형태다.
 * 껍데기(`FloatingPopup`)는 홈에 하나이고(`home-floating-popups.tsx`), 이 캠페인의 저장소 · 마크업만 여기 둔다.
 *
 * **기간이 없다**(VRChat 과 같은 상시 — 2026-10-29). 조건은 「한국 아님」 · 「오늘 안 닫음」 · 문구 준비다.
 *
 * ★**한국은 띄우지 않는다**(사용자 결정 2026-10-29). 판정은 commerce country(`bh_ccode` — `useLocale().countryCode`)이고,
 * 쿠키에서 확정되기 전(`isCountryHydrated` false)엔 띄우지 않는다. 그 전의 `countryCode` 는 빈 값이 아니라 **URL 로케일
 * 폴백**(`/en/` → `US`)이라, 한국 쿠키로 `/en/` 에 온 방문자에게 잠깐 떴다 사라질 수 있다(#6128 R1 — 「빈 값이면 모름」은
 * 닿지 않는 분기였다). 근거는 `locale-store.ts` 의 `isCountryHydrated` 주석.
 *
 * ```
 *   국가 확정 전 · KR                → 안 띄운다
 *   번들이 판매 목록에 없음           → 안 띄운다(어드민 판매중지 · 이 국가 가격 없음 · 게임 키 재고 없음 · 목록 로딩 중)
 *   오늘 안 닫음 + 문구 준비          → 슬라이드
 *   문구 전(COPY_READY false) · prod → 안 띄운다(빈 카드를 내보내지 않는다)
 *   문구 전 · 비-prod               → 띄운다(배경만) — 시안 대조용
 * ```
 */
export function useExoshockPopupSlides(): PopupSlide[] {
  const dismissedUntil = useExoshockPopupStore((s) => s.dismissedUntil);
  const dismiss = useExoshockPopupStore((s) => s.dismiss);
  const { countryCode, isCountryHydrated } = useLocale();
  const validBundles = useValidBundles();

  if (!isCountryHydrated || countryCode === "KR") return [];
  // ★광고하는 번들이 지금 팔리는가 — 번들 노출의 단일 판정(`useValidBundles`, docs/bundle-region-policy.md)을 그대로 쓴다.
  // 어드민에서 판매중지면 공개 상품 API 에서 레코드째 빠지고(2026-10-30 실측: prod 40개에 없음 · dev 41개에 있음), 가격이
  // 없는 국가 · 게임 키 재고가 없으면 목록에서 빠진다 — 그때 팝업이 닫힌 PDP(`/tactsuit/` 리다이렉트)로 보내지 않게.
  // 상품 캐시(서버 5분) 때문에 어드민에서 연 뒤 최대 몇 분 늦게 뜬다.
  if (!validBundles.some((b) => b.slug === EXOSHOCK_POPUP_BUNDLE_SLUG))
    return [];
  if (isDismissedToday(dismissedUntil)) return [];
  if (!EXOSHOCK_POPUP_COPY_READY && isProd) return [];

  return [
    {
      key: "exoshock",
      // 제품명이라 번역하지 않는다(조직 규칙: 공식 영문 제품명 그대로)
      label: "EXOSHOCK",
      onDismissToday: dismiss,
      node: <ExoshockSlide />,
    },
  ];
}

/**
 * Figma `Pop-up Card (Desktop) — Default` (`31842:70086`) · 모바일 시트 `31842:70159` — 360×190 슬롯 + 셸 푸터 40.
 * 2026-10-30 시안 재확인(디자이너 수정본) 기준.
 *
 * ```
 *   배경   exoshock-bundle-popup-bg 720×380 글자 없는 판 · cover · 대체색 #03080C
 *   글     왼쪽 16 · 세로 가운데 · 폭 180
 *     headline (gap 8)
 *       배지     LIMITED EDITION · bg #E31E24 · px8 py3 · r4 · 10/14 700 .8 white   (Badge / Limited Edition)
 *       text (gap 4)
 *         eyebrow  TactSuit Air: Onyx    12/16 400 .4   white
 *         title    bHaptics × EXOSHOCK   14/18 600 .25  white
 *     ↓ 16
 *     cta (gap 12)
 *       CTA      Learn More · h40 px24 r44 bg Blue/60 #0F62FE · 12/16 600 .1
 *       note     10/14 400 .4 white · 시안 줄바꿈 2줄(「… + badge」 / 「+ game key …」)
 * ```
 *
 * ★글자 · 배지를 이미지에 굽지 않는다 — 7개 언어로 나간다(시안 주석: 「라이브 글자 — 번역 가능」). 폭 180 은 오른쪽 그림
 * (EXOSHOCK 수트 · 케이스)을 피하는 값이다. 캡션은 시안대로 CTA **아래**다(기획이 위로 올려 달라 했던 것을 디자이너가 아래로 확정).
 */
function ExoshockSlide() {
  const { t } = useTrans(["event"]);

  return (
    <div className="relative h-[190px] bg-[#03080C]">
      <Image
        src={EXOSHOCK_POPUP_BG}
        alt=""
        fill
        sizes="360px"
        className="object-cover"
        priority
      />
      {EXOSHOCK_POPUP_COPY_READY && (
        <div className="absolute inset-y-0 left-4 flex w-[180px] flex-col justify-center gap-4 text-white">
          <div className="flex flex-col items-start gap-2">
            <span className="rounded bg-[#E31E24] px-2 py-[3px] text-2xs font-700 tracking-[0.8px] text-white">
              {t("exoshockPopupBadge")}
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-400 tracking-[0.4px]">
                {t("exoshockPopupEyebrow")}
              </p>
              <p className="text-base font-600 tracking-[0.25px]">
                {t("exoshockPopupTitle")}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-3">
            {EXOSHOCK_POPUP_CTA_HREF && (
              <Link
                href={EXOSHOCK_POPUP_CTA_HREF}
                className="inline-flex h-10 w-fit items-center justify-center rounded-[44px] bg-[#0F62FE] px-6 text-sm font-600 tracking-[0.1px] text-white"
              >
                {t("exoshockPopupCta")}
              </Link>
            )}
            <p className="whitespace-pre-line text-2xs font-400 tracking-[0.4px]">
              {t("exoshockPopupNote")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
